import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface UserFinanceStatus {
  totalPaid: number;
  isMember: boolean;
  loading: boolean;
  hasAnnualSub: boolean;
  hasPaidForEvent: boolean;
}

export const useUserFinance = (userId: string | undefined, eventId?: string): UserFinanceStatus => {
  const [status, setStatus] = useState<UserFinanceStatus>({
    totalPaid: 0,
    isMember: false,
    loading: true,
    hasAnnualSub: false,
    hasPaidForEvent: false
  });

  useEffect(() => {
    if (!userId) {
      setStatus({
        totalPaid: 0,
        isMember: false,
        loading: false,
        hasAnnualSub: false,
        hasPaidForEvent: false
      });
      return;
    }

    const fetchFinanceStatus = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const MEMBERSHIP_THRESHOLD = 10;

        const subscriptionsRef = collection(db, 'subscriptions');
        const q = query(
          subscriptionsRef,
          where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(q);

        let totalPaid = 0;
        let hasAnnualSub = false;
        let hasPaidForEvent = false;

        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          const amount = Number(data.amount) || 0;

          // Determine payment year
          let paymentDate: Date;
          if (data.paymentDate instanceof Timestamp) {
            paymentDate = data.paymentDate.toDate();
          } else if (typeof data.paymentDate === 'string') {
            paymentDate = new Date(data.paymentDate);
          } else {
            paymentDate = new Date(); // Fallback
          }

          if (paymentDate.getFullYear() === currentYear) {
            totalPaid += amount;
            if (data.type === 'annual') {
              hasAnnualSub = true;
            }
            if (eventId && data.type === 'event' && data.eventId === eventId) {
              hasPaidForEvent = true;
            }
          }
        });

        const isMember = hasAnnualSub || totalPaid >= MEMBERSHIP_THRESHOLD;

        setStatus({
          totalPaid,
          isMember,
          loading: false,
          hasAnnualSub,
          hasPaidForEvent
        });

      } catch (error) {
        console.error('Error fetching user finance status:', error);
        setStatus(prev => ({ ...prev, loading: false }));
      }
    };

    fetchFinanceStatus();
  }, [userId]);

  return status;
};
